import { drawing as d, geometry as g } from '@progress/kendo-drawing';
import { Element } from './Element';
import { AutoSizeableMixin } from './AutoSizeableMixin';
import { isNumber } from './../utils';
import { deepExtend } from './../utils/deep-extend';
/**
 * Represents a group of visual elements that can be managed and manipulated as a single unit.
 * Extends Element and incorporates AutoSizeableMixin functionality for automatic sizing.
 */
export class Group extends Element {
    /**
     * Creates a new Group instance.
     * @param options Configuration options for the group
     */
    constructor(options) {
        options = deepExtend({ autoSize: false }, options);
        super(options);
        /**
         * Array of child visual elements contained in this group.
         */
        this.children = [];
        /** @hidden */
        this._childrenChange = false;
        // Bind mixin methods to current instance
        this._setScale = AutoSizeableMixin._setScale.bind(this);
        this._setTranslate = AutoSizeableMixin._setTranslate.bind(this);
        this._initSize = AutoSizeableMixin._initSize.bind(this);
        this._updateSize = AutoSizeableMixin._updateSize.bind(this);
        this.drawingElement = new d.Group();
        this._initSize();
    }
    /**
     * Appends a visual element to this group.
     * @param visual The visual element to append to the group
     */
    append(visual) {
        this.drawingElement.append(visual.drawingContainer());
        this.children.push(visual);
        this._childrenChange = true;
    }
    ;
    /**
     * Removes a visual element from this group.
     * @param visual The visual element to remove from the group
     */
    remove(visual) {
        if (this._remove(visual)) {
            this._childrenChange = true;
        }
    }
    /** @hidden */
    _remove(visual) {
        const index = this.children.indexOf(visual);
        if (index >= 0) {
            this.drawingElement.removeAt(index);
            this.children.splice(index, 1);
            return true;
        }
    }
    /**
     * Removes all child visual elements from this group.
     */
    clear() {
        this.drawingElement.clear();
        this.children = [];
        this._childrenChange = true;
    }
    /**
     * Moves the specified visual elements to the front of the display order.
     * @param visuals Array of visual elements to move to front
     */
    toFront(visuals) {
        let visual;
        for (let i = 0; i < visuals.length; i++) {
            visual = visuals[i];
            if (this._remove(visual)) {
                this.append(visual);
            }
        }
    }
    // TO DO: add drawing group support for moving and inserting children
    /**
     * Moves the specified visual elements to the back of the display order.
     * @param visuals Array of visual elements to move to back
     */
    toBack(visuals) {
        this._reorderChildren(visuals, 0);
    }
    /**
     * Moves the specified visual elements to specific indices in the display order.
     * @param visuals Array of visual elements to reorder
     * @param indices Target indices for the visual elements
     */
    toIndex(visuals, indices) {
        this._reorderChildren(visuals, indices);
    }
    /** @hidden */
    _reorderChildren(visuals, indices) {
        const group = this.drawingElement;
        const drawingChildren = group.children.slice(0);
        const children = this.children;
        const fixedPosition = isNumber(indices);
        let i, index, toIndex, drawingElement, visual;
        for (i = 0; i < visuals.length; i++) {
            visual = visuals[i];
            drawingElement = visual.drawingContainer();
            index = children.indexOf(visual);
            if (index >= 0) {
                drawingChildren.splice(index, 1);
                children.splice(index, 1);
                toIndex = fixedPosition ? indices : indices[i];
                drawingChildren.splice(toIndex, 0, drawingElement);
                children.splice(toIndex, 0, visual);
            }
        }
        group.clear();
        group.append(...drawingChildren);
    }
    /**
     * Redraws the group with updated options.
     * @param options New options to apply to the group
     */
    redraw(options) {
        if (options) {
            if (this._childrenChange) {
                this._childrenChange = false;
                if (!this._updateSize(options)) {
                    this._initSize();
                }
            }
            else {
                this._updateSize(options);
            }
            super.redraw(options);
        }
    }
    /** @hidden */
    _boundingBox() {
        const children = this.children;
        let boundingBox;
        let visual, childBoundingBox;
        for (let i = 0; i < children.length; i++) {
            visual = children[i];
            if (visual.visible() && visual._includeInBBox !== false) {
                childBoundingBox = visual.drawingContainer().clippedBBox(null);
                if (childBoundingBox) {
                    if (boundingBox) {
                        boundingBox = g.Rect.union(boundingBox, childBoundingBox);
                    }
                    else {
                        boundingBox = childBoundingBox;
                    }
                }
            }
        }
        return boundingBox;
    }
}
